<template>
  <div v-clickoutside="$_onClickoutSide" :class="classes">
    <div :class="inputWrapperClass">
      <input
        ref="element"
        v-model="model"
        :placeholder="placeholder || t('t.timePicker.placeholder')"
        :disabled="disabled"
        :class="inputClass"
        type="text"
        readonly
        @focus="$_onFocus"
        @blur="$_onBlur"
        @click="$_onClickShow"
      />
      <span v-if="icon" :class="iconClasses" @click="$_onIconClick">
        <t-icon icon="close-circle"></t-icon>
      </span>
    </div>
    <transition name="fade">
      <div v-show="visible" ref="popper" :class="bodyClass" :style="popperStyle">
        <div :class="prefixCls + '__box'">
          <t-time-col v-if="showHour" v-model="hour" :list="hourOptions" type="H" @on-select="$_onSelect"></t-time-col>
          <t-time-col v-if="showMinute" v-model="minute" :list="minuteOptions" type="m" @on-select="$_onSelect"></t-time-col>
          <t-time-col v-if="showSecond" v-model="second" :list="secondOptions" type="s" @on-select="$_onSelect"></t-time-col>
        </div>
        <div v-if="confirm" :class="prefixCls + '__addon'">
          <slot name="addon">
            <t-button color="primary" size="sm" @click="$_onConfirm">{{ t('t.timePicker.confirm') }}</t-button>
          </slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Bus from '@/utils/bus'
import Popper from 'popper.js'
import { TButton } from '@/module/button'
import TIcon from '@/module/icon'
import locale from '@/mixins/locale'
import Emitter from '../../mixins/emitter'
import clickoutside from '@/directives/click-outside'

import {
  PREFIX_CLASS
} from '@/utils/constants'
import moment from 'moment'
import TTimeCol from './col'

const formatOption = (option, disabledOptions) => {
  let value = `${option}`
  if (option < 10) {
    value = `0${option}`
  }

  let disabled = false
  if (disabledOptions && disabledOptions.indexOf(option) >= 0) {
    disabled = true
  }

  return {
    value,
    disabled
  }
}

const generateOptions = (length, disabledOptions, hideDisabledOptions, step = 1) => {
  const arr = []
  for (let value = 0; value < length; value += step) {
    if ((!disabledOptions || disabledOptions.indexOf(value) === -1) && !hideDisabledOptions) {
      arr.push(value)
    }
  }
  return arr
}

const PREFIXCLS = `${PREFIX_CLASS}time-picker`
export default {
  name: 'TTimePicker',
  components: {
    TTimeCol,
    TButton,
    TIcon
  },
  directives: { clickoutside },
  mixins: [ locale, Emitter ],
  props: {
    value: {
      type: String,
      default: ''
    },
    format: {
      type: String,
      default: 'HH:mm:ss'
    },
    position: {
      type: String,
      validator(value) {
        /*
         * 有以下值：
         * 'top',
         * 'top-start',
         * 'top-end',
         * 'bottom',
         * 'bottom-start',
         * 'bottom-end',
         * 'left',
         * 'left-start',
         * 'left-end',
         * 'right',
         * 'right-start',
         * 'right-end'
         * https://popper.js.org/popper-documentation.html#Popper.placements
         */
        return Popper.placements.includes(value)
      },
      default: 'bottom-start'
    },
    placeholder: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      validator(value) {
        /* istanbul ignore next */
        return ['sm', 'lg', ''].includes(value)
      },
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    confirm: {
      type: Boolean,
      default: true
    },
    disabledHours: {
      type: Array,
      default: () => []
    },
    disabledMinutes: {
      type: Array,
      default: () => []
    },
    disabledSeconds: {
      type: Array,
      default: () => []
    },
    hideDisabledHourOptions: {
      type: Boolean,
      default: false
    },
    hideDisabledMinuteOptions: {
      type: Boolean,
      default: false
    },
    hideDisabledSecondOptions: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      prefixCls: PREFIXCLS,
      visible: false,
      minWidth: '',
      hourOptions: [],
      minuteOptions: [],
      secondOptions: [],
      hour: 0,
      minute: 0,
      second: 0,
      time: null,
      icon: false
    }
  },
  computed: {
    model: {
      get () {
        const time = this.value ? moment(this.value, this.format) : ''
        return time ? time.format(this.format) : ''
      },
      set (val) {
        // NOTE: 用于form验证
        this.$emit('input', val)
        this.$emit('change', val)
        this.dispatch('TFormItem', 'g-on-form-change', val)
      }
    },
    popperStyle() {
      return {
        'max-width': this.minWidth
      }
    },
    classes () {
      return [`${this.prefixCls}`]
    },
    inputWrapperClass () {
      return [`${this.prefixCls}__input-wrapper`]
    },
    inputClass () {
      return [
        `${this.prefixCls}__input`,
        {
          [`${this.prefixCls}__input--${this.size}`]: !!this.size,
          [`${this.prefixCls}__input--disabled`]: this.disabled
        }
      ]
    },
    bodyClass () {
      return [`${this.prefixCls}__body`]
    },
    iconClasses () {
      return [`${this.prefixCls}__input-close`]
    },
    showHour () {
      return this.format.indexOf('H') > -1 ||
        this.format.indexOf('h') > -1 ||
        this.format.indexOf('k') > -1
    },
    showMinute () {
      return this.format.indexOf('m') > -1
    },
    showSecond () {
      return this.format.indexOf('s') > -1
    }
  },
  watch: {
    model (val) {
      this.icon = this.clearable && !!val
    }
  },
  mounted() {
    this.time = this.model ? moment(this.model, this.format) : moment('00:00:00', this.format)
    this.hour = this.time.hour()
    this.minute = this.time.minute()
    this.second = this.time.second()

    this.hourOptions = generateOptions(24, this.disabledHours, this.hideDisabledHourOptions)
      .map(option => formatOption(option, this.hideDisabledHourOptions))

    this.minuteOptions = generateOptions(
      60,
      this.disabledMinutes,
      this.hideDisabledMinuteOptions
    ).map(option => formatOption(option, this.hideDisabledMinuteOptions))

    this.secondOptions = generateOptions(
      60,
      this.disabledSeconds,
      this.hideDisabledSecondOptions
    ).map(option => formatOption(option, this.hideDisabledSecondOptions))

    this.popper = new Popper(this.$refs.element, this.$refs.popper, {
      placement: this.position
    })
    this.$_updateMinWidth()
    this.icon = this.clearable && !!this.model
  },
  updated() {
    this.$_updateMinWidth()
  },
  methods: {
    $_updateMinWidth() {
      this.minWidth = window.getComputedStyle(this.$refs.element).getPropertyValue('width')
    },
    $_onFocus () {
      this.visible = true
      Bus.$emit('g-time-picker-visible')
    },
    $_onBlur () {
      // this.visible = false
    },
    $_onClickShow () {
      this.visible = true
      Bus.$emit('g-time-picker-visible')
    },
    $_onSelect (type, item) {
      if (type === 'H') {
        this.time.hour(+item)
        this.hour = this.time.hour()
      } else if (type === 'm') {
        this.time.minute(+item)
        this.minute = this.time.minute()
      } else {
        this.time.second(+item)
        this.second = this.time.second()
      }
      if (!this.confirm) {
        this.model = this.time.format(this.format)
      }
    },
    $_onConfirm () {
      this.model = this.time.format(this.format)
      this.visible = false
    },
    $_onClickoutSide (e) {
      this.visible = false
      return true
    },
    $_onIconClick () {
      this.model = ''
      this.time = moment('00:00:00', 'HH:mm:ss')
      this.hour = this.time.hour()
      this.minute = this.time.minute()
      this.second = this.time.second()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/module/time-picker/index.vue